"use client";

import { useState } from "react";
import { httpsCallable } from "firebase/functions";
import { functions } from "@/lib/firebase";
import { useTranslations } from "@/i18n/LocaleProvider";

/**
 * Rendered instead of a real gateway checkout while DEMO_MODE is on (see
 * functions/src/payments/gateways/demo.ts) -- a visible, explicit step rather than an instant
 * auto-confirm, so a live demo actually shows what a customer's payment step looks like end to
 * end, not just the outcome.
 */
export default function DemoPaymentStep({
  gatewayOrderId,
  bookingReference,
  amountRupees,
  onConfirmed,
}: {
  gatewayOrderId: string;
  bookingReference: string;
  amountRupees: number;
  onConfirmed: () => void;
}) {
  const { t } = useTranslations();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSimulate() {
    setLoading(true);
    setError(null);
    try {
      const confirmDemoPayment = httpsCallable<{ gatewayOrderId: string }, { ok: true }>(
        functions,
        "confirmDemoPayment"
      );
      await confirmDemoPayment({ gatewayOrderId });
      onConfirmed();
    } catch (err) {
      setError(err instanceof Error ? err.message : t("demoPayment.simulateError"));
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="mt-8 space-y-4 rounded-2xl border-2 border-dashed border-accent/50 bg-accent/5 p-6">
      <span className="inline-block rounded-full bg-accent px-3 py-1 text-xs font-semibold uppercase tracking-wide text-accent-foreground">
        {t("demoPayment.badge")}
      </span>
      <p className="text-sm text-foreground/70">
        {t("demoPayment.notice", {
          reference: bookingReference,
          amount: amountRupees.toLocaleString("en-IN"),
        })}
      </p>
      {error && <p className="text-sm text-red-700">{error}</p>}
      <button
        onClick={() => void handleSimulate()}
        disabled={loading}
        className="rounded-xl bg-accent px-6 py-3 font-semibold text-accent-foreground disabled:opacity-50"
      >
        {loading ? t("demoPayment.simulateLoading") : t("demoPayment.simulateIdle")}
      </button>
    </div>
  );
}
